<template>
	<VDataGridV2
		:loader="dataLoader"
		:context-menu="ShipmentsService.contextMenu"
		paginated
		entity-type="shipment"
		@row:dblclick="openShipmentCard"
	>
		<VDataGridColumnV2
			id="id"
			width="80px"
	label="№"
			sortable
			sort-default-value="desc"
			filterable
			filter-send-name="idPattern"
			:filter-props="{ placeholder: '№', clearable: true }"
		>
			<template #default="{ row }">
				<VLink @click="openShipmentCard(row)">{{ row.formattedNumber }}</VLink>
				<VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="seller"
			width="180px"
			content-class="ellipsis"
			sortable
		>
			<template #label>
				<div>Грузоотправитель</div>
				<div class="description">Юр. лицо</div>
			</template>
			<template #default="{ row }">
				<div class="ellipsis" :title="row.seller.name">
					{{ row.seller.name }}
				</div>
				<div class="description ellipsis" :title="row.sellerCompany.name">
					{{ row.sellerCompany.name }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="buyer"
	        width="180px"
			filterable
	        filter-identity="*"
			filter-type="select"
			filter-send-name="buyer"
			:filter-props="{ placeholder: 'Название партнера', searchFunction: PartnersService.searchBuyers, clearable: true }"
			content-class="ellipsis"
		>
			<template #label>
				<div>Грузополучатель</div>
				<div class="description">Юр. лицо</div>
			</template>
			<template #default="{ row }">
				<div class="ellipsis" :title="row.buyer.name">
					{{ row.buyer.name }}
				</div>
				<div class="description ellipsis" :title="row.buyerCompany.name">
					{{ row.buyerCompany.name }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="documentNumber"
			filterable
			sortable
			filter-send-name="documentNumberPattern"
			:filter-props="{ placeholder: 'Номер', clearable: true }"
			width="100px"
			label="Номер документа"
			content-class="ellipsis"
			header-class="nowrap"
		>
			<template #default="{ row }">
				<div class="ellipsis" :title="`${row.documentType.name } №${row.documentNumber}`">
					{{ row.documentType.name }} №{{ row.documentNumber }}
				</div>
				<div class="nowrap description">
					{{ $core.utils.date.format(row.documentDate) }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="deliveryType"
			width="100px"
			label="Тип доставки"
			content-align="center"
		>
			<template #default="{ row }">
				<div>{{ row.deliveryType.name }}</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="sumFull"
			content-align="right"
			width="120px"
			label="Сумма"
			content-class="nowrap"
		>
			<template #default="{ row }">
	  {{ row.sumFull.format(row.orderCurrency) }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="accountingSum"
			content-align="right"
			width="120px"
			content-class="nowrap"
			header-class="nowrap"
		>
			<template #label>
					Сумма, {{ $core.utils.money.getCurrencyByCode($core.acl.currentPartnerCurrencyCode).symbol }}
			</template>
			<template #default="{ row }">
				<div class="nowrap">
					{{ row.accountingSumFull?.format(row.accountingCurrency) || row.accountingSumFullCalculated?.format(row.accountingCurrency) }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isVerifiedState"
			content-align="center"
			filterable
			filter-type="select"
			filter-send-name="isVerifiedState"
			:filter-props="{
				options: [
					{ id: 1, isVerified: true,  name: 'Да'  },
					{ id: 2, isVerified: false, name: 'Нет' },
				],
			}"
			filter-identity="isVerified"
			width="80px"
			label="Сверен"
		>
			<template #default="{ row }">
				{{ row.isVerified ? 'Да' : 'Нет' }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="plannedShipmentDate"
			filterable
			sortable
			width="100px"
			filter-type="date-range"
			filter-send-name="shipmentPeriod"
			content-align="center"
			content-class="nowrap"
			:filter-props="{ placeholder: 'Введите диапазон', clearable: true }"
			label="Дата отгрузки"
		>
			<template #default="{ row }">
					{{ $core.utils.date.humanizeDate(row.shipmentDate, null) }}
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isReceived"
			content-align="center"
			width="40px"
			filterable
			filter-type="select"
			:filter-props="{
				options: [
					{ id: 1, isReceived: true,  name: 'Да'  },
					{ id: 2, isReceived: false, name: 'Нет' },
				],
			}"
			filter-identity="isReceived"
		>
			<template #label>
				<div>Принят</div>
				<div class="description">Дата</div>
			</template>
			<template #default="{ row }">
				<div class="nowrap">{{ row.receiptDate ? 'Да' : 'Нет' }}</div>
				<div class="description">
					{{ row.receiptDate?.date?.format() }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="externalNumber"
			filterable
			filter-send-name="externalNumberPattern"
			:filter-props="{ placeholder: 'Номер', clearable: true }"
			width="40px"
			content-class="ellipsis"
			header-class="nowrap"
			label="№ Техно"
		>
			<template #default="{ row }">
				<div class="ellipsis" :title="row.externalNumber || 'Не задано'">
					{{ row.externalNumber || 'Не задано' }}
				</div>
			</template>
		</VDataGridColumnV2>

		<VDataGridColumnV2
			id="isOfferBasedState"
			label="Прямой"
			content-align="center"
			header-class="nowrap"
			filterable
			filter-type="select"
			:filter-props="{
				options: [
					{ id: 1, name: 'Да',  isOfferBased: false },
					{ id: 2, name: 'Нет', isOfferBased: true  },
				],
				clearable: true,
			}"
			filter-identity="isOfferBased"
			width="90px"
		>
			<template #default="{ row }">
				<template v-if="row.isOfferBased === false">Да</template>
				<template v-else>Нет</template>
			</template>
		</VDataGridColumnV2>
	</VDataGridV2>
</template>

<script lang="ts">

import PaymentReasonType          from '@/domains/PaymentRequests/PaymentReasonType';
import Shipment                   from '@/domains/Shipments/Shipment'
import ShipmentsService           from '@/domains/Shipments/ShipmentsService';
import PartnersService            from "@/domains/Partners/PartnersService";
import DataGridLoader             from "@/lib/components/VDataGridV2/DataGridLoader";

export default {
name: 'ShipmentsRegistry',

setup() {
	return {
		ShipmentsService,
		PartnersService,
	};
},

data() {
	return {

		showFilter: false,

		dataLoader: new DataGridLoader(ShipmentsService.getListPaginated),

		shipmentPositions: [],

		paymentReasonType: PaymentReasonType
	};
},

mounted() {
	this.showFilter = true;
},

methods: {
	async openShipmentCard(row: Shipment): Promise<void> {
  if (await this.$core.dialogs.ShipmentCard.showAsPromise({id: row.id})) {
	this.dataLoader.refresh();
  }
	},
},
};
</script>